import React from 'react';
import { motion } from 'framer-motion';
import {
  TrendingDown,
  Truck,
  MapPin,
  DollarSign,
  Package,
  Lightbulb,
  Award,
  AlertCircle,
  CheckCircle,
  Info
} from 'lucide-react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartTooltip,
  Legend as RechartLegend,
  ResponsiveContainer,
  Cell,
  BarChart,
  Bar as BarRechart
} from 'recharts';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

interface FreightAIInsightsDisplayProps {
  data: any;
  isLoading?: boolean;
  error?: any;
}

const FreightAIInsightsDisplay: React.FC<FreightAIInsightsDisplayProps> = ({ data, isLoading, error }) => {
  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex items-center justify-center py-20 rounded-xl"
        style={{ backgroundColor: 'var(--bg-secondary)' }}
      >
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-4"
            style={{ borderColor: 'var(--primary)' }}
          ></div>
          <p className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>
            🤖 Processando análise de fretes...
          </p>
        </div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl p-6 text-center border-l-4"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderColor: '#ef4444'
        }}
      >
        <AlertCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#ef4444' }} />
        <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
          Erro ao carregar análise
        </h3>
        <p style={{ color: 'var(--text-secondary)' }}>
          {error?.message || 'Erro desconhecido'}
        </p>
      </motion.div>
    );
  }

  if (!data) {
    return null;
  }

  const { summary, business_units_analysis, vehicle_analysis, strategic_insights } = data;

  // KPI Cards Data
  const kpis = [
    {
      title: 'Custo Total de Fretes',
      value: `R$ ${summary.total_freight_cost?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0,00'}`,
      icon: DollarSign,
      gradient: 'from-blue-500 to-blue-600',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600'
    },
    {
      title: 'Total de Entregas',
      value: summary.total_shipments?.toLocaleString('pt-BR') || '0',
      icon: Package,
      gradient: 'from-green-500 to-green-600',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600'
    },
    {
      title: 'Distância Total',
      value: `${(summary.total_distance_km || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} km`,
      icon: MapPin,
      gradient: 'from-purple-500 to-purple-600',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600'
    },
    {
      title: 'Potencial de Economia',
      value: `${summary.cost_reduction_potential?.toFixed(2) || '0'}%`,
      icon: TrendingDown,
      gradient: 'from-orange-500 to-orange-600',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600'
    }
  ];

  // Vehicle Distribution Chart
  const vehicleChartData = {
    labels: Object.keys(vehicle_analysis?.vehicle_types || {}),
    datasets: [
      {
        data: Object.values(vehicle_analysis?.vehicle_types || {}),
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(251, 146, 60, 0.8)'
        ],
        borderColor: [
          'rgb(59, 130, 246)',
          'rgb(16, 185, 129)',
          'rgb(251, 146, 60)'
        ],
        borderWidth: 2
      }
    ]
  };

  // Business Units Chart
  const businessUnitsChartData = {
    labels: business_units_analysis?.units_with_calculations?.map((u: any) => u.business_unit_code) || [],
    datasets: [
      {
        label: 'Custo Total (R$)',
        data: business_units_analysis?.units_with_calculations?.map((u: any) => u.total_freight) || [],
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 2
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          callback: function(value: any) {
            return 'R$ ' + value.toLocaleString('pt-BR');
          }
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* KPI Cards - Elegante e Premium */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {kpis.map((kpi, index) => {
          const gradients = [
            'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            'linear-gradient(135deg, #a855f7 0%, #7e22ce 100%)',
            'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
          ];

          return (
            <motion.div
              key={kpi.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="group rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
              style={{
                background: gradients[index],
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <div className="p-6 text-white">
                <div className="flex items-start justify-between mb-4">
                  <span className="text-sm font-medium tracking-wide opacity-90">
                    {kpi.title}
                  </span>
                  <kpi.icon className="w-6 h-6 opacity-80 group-hover:scale-110 transition-transform" />
                </div>
                <p className="text-4xl font-bold mb-2">{kpi.value}</p>
                <div className="h-1 w-full rounded-full opacity-30" style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)' }} />
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Strategic Insights - Elegante */}
      {strategic_insights && strategic_insights.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div
            className="rounded-xl p-8 overflow-hidden"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: `1px solid var(--border-primary)`
            }}
          >
            <div className="flex items-center gap-3 mb-6">
              <Lightbulb className="w-6 h-6" style={{ color: 'var(--primary)' }} />
              <h3 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                💡 Insights Estratégicos
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {strategic_insights.map((insight: any, index: number) => {
                const getTypeConfig = (type: string) => {
                  switch (type) {
                    case 'success':
                      return { border: '#10b981', icon: CheckCircle, bgLight: '#d1fae5' };
                    case 'warning':
                      return { border: '#f59e0b', icon: AlertCircle, bgLight: '#fef3c7' };
                    case 'alert':
                      return { border: '#ef4444', icon: AlertCircle, bgLight: '#fee2e2' };
                    default:
                      return { border: '#3b82f6', icon: Info, bgLight: '#eff6ff' };
                  }
                };
                const config = getTypeConfig(insight.type);
                const IconComponent = config.icon;

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.45 + index * 0.05 }}
                    className="rounded-lg overflow-hidden transition-all hover:shadow-md"
                    style={{
                      backgroundColor: 'var(--bg-tertiary)',
                      borderLeft: `4px solid ${config.border}`
                    }}
                  >
                    <div className="p-5">
                      <div className="flex items-start gap-3 mb-3">
                        <div
                          className="p-2 rounded-lg flex-shrink-0"
                          style={{ backgroundColor: config.bgLight }}
                        >
                          <IconComponent className="w-4 h-4" style={{ color: config.border }} />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>
                            {insight.title}
                          </h4>
                        </div>
                      </div>
                      <p className="text-xs leading-relaxed mb-3" style={{ color: 'var(--text-secondary)' }}>
                        {insight.description}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <span
                          className="text-xs font-medium px-2 py-1 rounded-full"
                          style={{
                            backgroundColor: `${config.border}20`,
                            color: config.border
                          }}
                        >
                          {insight.impact || 'info'}
                        </span>
                        <span
                          className="text-xs font-medium px-2 py-1 rounded-full"
                          style={{
                            backgroundColor: 'var(--bg-secondary)',
                            color: 'var(--text-secondary)'
                          }}
                        >
                          {(insight.confidence * 100).toFixed(0)}% confiança
                        </span>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}

      {/* Vehicle Analysis - Moderno */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <div
          className="rounded-xl p-8 overflow-hidden"
          style={{
            backgroundColor: 'var(--bg-secondary)',
            border: `1px solid var(--border-primary)`
          }}
        >
          <div className="flex items-center gap-3 mb-8">
            <Truck className="w-6 h-6" style={{ color: 'var(--primary)' }} />
            <h3 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              🚛 Análise de Veículos
            </h3>
          </div>

          {/* Most Efficient Vehicle & Chart */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Most Efficient Vehicle */}
            {vehicle_analysis?.most_efficient_vehicle && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.65 }}
                className="rounded-xl overflow-hidden shadow-lg"
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                <div className="p-8 text-white">
                  <div className="flex items-center gap-2 mb-6">
                    <Award className="w-6 h-6" />
                    <h4 className="text-xl font-bold">Veículo Mais Eficiente</h4>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <p className="text-green-100 text-sm font-medium mb-1">Tipo de Veículo</p>
                      <p className="text-3xl font-bold">
                        {vehicle_analysis.most_efficient_vehicle.vehicle_name}
                      </p>
                    </div>

                    <div className="pt-4 border-t border-green-300 border-opacity-20">
                      <p className="text-green-100 text-sm font-medium mb-1">Custo Médio por Km</p>
                      <p className="text-2xl font-bold">
                        R$ {vehicle_analysis.most_efficient_vehicle.avg_cost_per_km.toFixed(2)}/km
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4 pt-4">
                      <div className="bg-green-500 bg-opacity-30 rounded-lg p-4">
                        <p className="text-green-100 text-xs font-medium">Utilizações</p>
                        <p className="text-2xl font-bold">
                          {vehicle_analysis.most_efficient_vehicle.count}
                        </p>
                      </div>
                      <div className="bg-green-500 bg-opacity-30 rounded-lg p-4">
                        <p className="text-green-100 text-xs font-medium">Distância</p>
                        <p className="text-2xl font-bold">
                          {(vehicle_analysis.most_efficient_vehicle.total_distance / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} km
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Vehicle Distribution Chart */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.65 }}
            >
              <h4 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                📊 Distribuição de Veículos
              </h4>
              <div
                className="rounded-lg p-6"
                style={{
                  backgroundColor: 'var(--bg-tertiary)',
                  height: '300px'
                }}
              >
                <Doughnut data={vehicleChartData} options={{ responsive: true, maintainAspectRatio: false }} />
              </div>
            </motion.div>
          </div>

          {/* Vehicle Comparison Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <h4 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              📋 Comparação de Veículos
            </h4>
            <div className="overflow-x-auto rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
              <table className="w-full">
                <thead>
                  <tr
                    style={{
                      backgroundColor: 'var(--bg-secondary)',
                      borderBottom: `2px solid var(--border-primary)`
                    }}
                  >
                    <th className="text-left py-4 px-5 font-bold" style={{ color: 'var(--text-primary)' }}>
                      Veículo
                    </th>
                    <th className="text-right py-4 px-5 font-bold" style={{ color: 'var(--text-primary)' }}>
                      Utilizações
                    </th>
                    <th className="text-right py-4 px-5 font-bold" style={{ color: 'var(--text-primary)' }}>
                      Custo Total
                    </th>
                    <th className="text-right py-4 px-5 font-bold" style={{ color: 'var(--text-primary)' }}>
                      Custo/km
                    </th>
                    <th className="text-right py-4 px-5 font-bold" style={{ color: 'var(--text-primary)' }}>
                      Distância
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {vehicle_analysis?.vehicles_with_analysis?.map((vehicle: any, index: number) => (
                    <tr
                      key={index}
                      style={{
                        borderBottom: `1px solid var(--border-primary)`,
                        backgroundColor: index % 2 === 0 ? 'transparent' : 'var(--bg-secondary)'
                      }}
                      className="transition-colors hover:bg-opacity-70"
                    >
                      <td className="py-4 px-5 font-semibold" style={{ color: 'var(--text-primary)' }}>
                        {vehicle.vehicle_name}
                      </td>
                      <td className="py-4 px-5 text-right">
                        <span
                          className="font-bold px-3 py-1 rounded-lg"
                          style={{
                            backgroundColor: 'var(--primary)',
                            color: 'var(--text-primary)'
                          }}
                        >
                          {vehicle.count}
                        </span>
                      </td>
                      <td className="py-4 px-5 text-right font-semibold" style={{ color: 'var(--text-primary)' }}>
                        R$ {vehicle.total_freight.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                      </td>
                      <td className="py-4 px-5 text-right font-bold" style={{ color: 'var(--primary)' }}>
                        R$ {vehicle.avg_cost_per_km.toFixed(2)}
                      </td>
                      <td className="py-4 px-5 text-right" style={{ color: 'var(--text-secondary)' }}>
                        {(vehicle.total_distance / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} km
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Vehicle Comparison Bar Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75 }}
          >
            <h4 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              📊 Comparação de Veículos
            </h4>
            <div
              className="rounded-lg p-6 w-full"
              style={{
                backgroundColor: 'var(--bg-tertiary)',
                minHeight: '350px'
              }}
            >
              {vehicle_analysis?.vehicles_with_analysis && vehicle_analysis.vehicles_with_analysis.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={vehicle_analysis.vehicles_with_analysis}
                    margin={{ top: 20, right: 80, bottom: 20, left: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary)" />
                    <XAxis
                      dataKey="vehicle_name"
                      stroke="var(--text-secondary)"
                      style={{ fontSize: '12px' }}
                    />
                    <YAxis
                      yAxisId="left"
                      stroke="var(--text-secondary)"
                      style={{ fontSize: '12px' }}
                      label={{ value: 'Utilizações', angle: -90, position: 'insideLeft' }}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      stroke="var(--text-secondary)"
                      style={{ fontSize: '12px' }}
                      label={{ value: 'Custo/km (R$)', angle: 90, position: 'insideRight' }}
                    />
                    <RechartTooltip
                      contentStyle={{
                        backgroundColor: 'var(--bg-card)',
                        border: '1px solid var(--border-primary)',
                        borderRadius: '8px',
                        color: 'var(--text-primary)'
                      }}
                      formatter={(value: any, name: string) => {
                        if (name === 'count') {
                          return [value, 'Utilizações'];
                        }
                        if (name === 'total_distance') {
                          return [(value / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 0 }), 'Distância (km)'];
                        }
                        if (name === 'total_freight') {
                          return [value.toLocaleString('pt-BR', { maximumFractionDigits: 0 }), 'Custo Total (R$)'];
                        }
                        if (name === 'avg_cost_per_km') {
                          return [value.toFixed(2), 'Custo/km (R$)'];
                        }
                        return [value, name];
                      }}
                    />
                    <RechartLegend
                      wrapperStyle={{ color: 'var(--text-secondary)', fontSize: '12px' }}
                    />
                    <BarRechart yAxisId="left" dataKey="count" fill="#FF006E" name="Utilizações" />
                    <BarRechart yAxisId="right" dataKey="avg_cost_per_km" fill="#00D9FF" name="Custo/km (R$)" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p style={{ color: 'var(--text-secondary)' }}>
                  Sem dados de veículos para exibir
                </p>
              )}
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Business Units Analysis - Moderno */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        <div
          className="rounded-xl p-8 overflow-hidden"
          style={{
            backgroundColor: 'var(--bg-secondary)',
            border: `1px solid var(--border-primary)`
          }}
        >
          <div className="flex items-center gap-3 mb-8">
            <MapPin className="w-6 h-6" style={{ color: 'var(--primary)' }} />
            <h3 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              📍 Análise por Unidade de Negócio
            </h3>
          </div>

          {/* Chart */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.85 }}
            className="mb-8 rounded-lg p-6"
            style={{
              backgroundColor: 'var(--bg-tertiary)',
              height: '350px'
            }}
          >
            <Bar data={businessUnitsChartData} options={chartOptions} />
          </motion.div>

          {/* Units Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {business_units_analysis?.units_with_calculations?.map((unit: any, index: number) => {
              const gradients = [
                'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(29, 78, 216, 0.15) 100%)',
                'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)',
                'linear-gradient(135deg, rgba(168, 85, 247, 0.15) 0%, rgba(126, 34, 206, 0.15) 100%)',
                'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.15) 100%)'
              ];

              const borderColors = [
                'rgba(59, 130, 246, 0.4)',
                'rgba(16, 185, 129, 0.4)',
                'rgba(168, 85, 247, 0.4)',
                'rgba(245, 158, 11, 0.4)'
              ];

              return (
                <motion.div
                  key={unit.business_unit_code}
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ delay: 0.9 + index * 0.05 }}
                  whileHover={{ y: -5 }}
                  className="group rounded-xl overflow-hidden transition-all hover:shadow-2xl"
                  style={{
                    background: gradients[index % 4],
                    border: `2px solid ${borderColors[index % 4]}`,
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <div className="p-6 relative overflow-hidden">
                    {/* Background decoration */}
                    <div
                      className="absolute top-0 right-0 w-32 h-32 opacity-5"
                      style={{
                        background: 'radial-gradient(circle, var(--primary), transparent)',
                        transform: 'translate(20%, -20%)'
                      }}
                    />

                    {/* Header with badge */}
                    <div className="flex items-start justify-between mb-6 relative z-10">
                      <div>
                        <h4 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                          {unit.business_unit_code || `Unidade ${index + 1}`}
                        </h4>
                        <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                          Unidade de Negócio
                        </p>
                      </div>
                      <motion.span
                        whileHover={{ scale: 1.1 }}
                        className="text-xs font-bold px-4 py-2 rounded-full text-white shadow-lg"
                        style={{
                          background: [
                            'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                            'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            'linear-gradient(135deg, #a855f7 0%, #7e22ce 100%)',
                            'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                          ][index % 4]
                        }}
                      >
                        {unit.calculations_count} 📊
                      </motion.span>
                    </div>

                    {/* Main Metric - Cost */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.95 + index * 0.05 }}
                      className="mb-5 p-4 rounded-lg backdrop-blur-md"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}
                    >
                      <p className="text-xs font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                        💰 CUSTO TOTAL
                      </p>
                      <p className="text-3xl font-bold" style={{ color: 'var(--primary)' }}>
                        R$ {(unit.total_freight || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                      </p>
                    </motion.div>

                    {/* Metrics Grid */}
                    <div className="grid grid-cols-2 gap-3 mb-5 relative z-10">
                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        className="p-4 rounded-lg transition-all cursor-pointer"
                        style={{
                          backgroundColor: 'rgba(59, 130, 246, 0.1)',
                          border: '1px solid rgba(59, 130, 246, 0.3)'
                        }}
                      >
                        <p className="text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                          📊 Custo/KM
                        </p>
                        <p className="text-lg font-bold" style={{ color: 'rgb(59, 130, 246)' }}>
                          R$ {(unit.avg_cost_per_km || 0).toFixed(2)}
                        </p>
                      </motion.div>

                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        className="p-4 rounded-lg transition-all cursor-pointer"
                        style={{
                          backgroundColor: 'rgba(16, 185, 129, 0.1)',
                          border: '1px solid rgba(16, 185, 129, 0.3)'
                        }}
                      >
                        <p className="text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                          📦 Entregas
                        </p>
                        <p className="text-lg font-bold" style={{ color: 'rgb(16, 185, 129)' }}>
                          {unit.total_shipments || 0}
                        </p>
                      </motion.div>

                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        className="p-4 rounded-lg transition-all cursor-pointer col-span-2"
                        style={{
                          backgroundColor: 'rgba(168, 85, 247, 0.1)',
                          border: '1px solid rgba(168, 85, 247, 0.3)'
                        }}
                      >
                        <p className="text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                          🗺️ Distância Total
                        </p>
                        <p className="text-lg font-bold" style={{ color: 'rgb(168, 85, 247)' }}>
                          {((unit.total_distance || 0) / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} km
                        </p>
                      </motion.div>
                    </div>

                    {/* Most Efficient Vehicle - Destaque */}
                    {unit.min_vehicle_type_name && (
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 1.0 + index * 0.05 }}
                        className="p-4 rounded-lg mb-5 shadow-md relative z-10"
                        style={{
                          background: 'linear-gradient(135deg, rgba(251, 146, 60, 0.3) 0%, rgba(234, 88, 12, 0.3) 100%)',
                          border: '2px solid rgba(251, 146, 60, 0.5)',
                          backgroundColor: 'transparent'
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="p-3 rounded-lg text-xl"
                            style={{ backgroundColor: 'rgba(251, 146, 60, 0.2)' }}
                          >
                            🏆
                          </div>
                          <div className="flex-1">
                            <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
                              Veículo Mais Eficiente
                            </p>
                            <p className="text-sm font-bold" style={{ color: 'rgb(251, 146, 60)' }}>
                              {unit.min_vehicle_type_name}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Vehicles Used */}
                    {unit.vehicles_used && unit.vehicles_used.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.05 + index * 0.05 }}
                        className="pt-4 relative z-10"
                        style={{
                          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                        }}
                      >
                        <h5 className="text-xs font-bold mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                          <span>🚛</span>
                          <span>VEÍCULOS UTILIZADOS ({unit.vehicles_used.length})</span>
                        </h5>
                        <div className="space-y-2">
                          {unit.vehicles_used.slice(0, 3).map((vehicle: any, vIndex: number) => (
                            <motion.div
                              key={vIndex}
                              whileHover={{ x: 4 }}
                              className="flex items-center justify-between p-3 rounded-lg transition-all"
                              style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                border: '1px solid rgba(255, 255, 255, 0.1)'
                              }}
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <span
                                  className="text-xs font-bold px-2 py-1 rounded-full text-white"
                                  style={{
                                    background: [
                                      'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                      'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                      'linear-gradient(135deg, #a855f7 0%, #7e22ce 100%)'
                                    ][vIndex % 3]
                                  }}
                                >
                                  {vehicle.quantity}x
                                </span>
                                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                  {vehicle.vehicle_type}
                                </span>
                              </div>
                              <span className="text-xs font-bold" style={{ color: 'var(--primary)' }}>
                                R$ {(vehicle.total_freight || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                              </span>
                            </motion.div>
                          ))}
                          {unit.vehicles_used.length > 3 && (
                            <p className="text-xs text-center mt-2" style={{ color: 'var(--text-secondary)' }}>
                              +{unit.vehicles_used.length - 3} outro(s) veículo(s)
                            </p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default FreightAIInsightsDisplay;

